import { z } from "zod";

import packageJSON from "../package.json";

const environments = z.enum(["production", "test", "development"]);
export type ENVIRONMENTS = z.infer<typeof environments>;

const stringSchema = z.string({
    error: "Not defined",
});
const urlSchema = z.url({
    error: "Invalid URL format",
});
const uuidSchema = z.uuid({
    error: "Invalid UUID format",
});

const configSchema = z.strictObject({
    /*
     * APP_ENV: Enum
     * Specifies the application's runtime environment.
     * Required: Yes | Default: "production"
     * Example (.env): VITE_APP_ENV=production
     */
    APP_ENV: environments.default("production"),

    /*
     * APP_NAME: String
     * Specifies the application's name.
     * Required: Yes | Default: "ARDI"
     * Example (.env): VITE_APP_NAME=ARDI
     */
    APP_NAME: stringSchema.default("ARDI"),
    /*
     * APP_VERSION: String
     * Specifies the application's version.
     * Required: Yes | Default: "v0.0.0"
     * Example (.env): VITE_APP_VERSION
     */
    APP_VERSION: stringSchema.default(packageJSON.version),

    /*
     * APP_RELEASE_NOTES_URL: String URL
     * Specifies the URL of the release notes.
     * Required: Yes
     * Example (.env): VITE_APP_RELEASE_NOTES_URL=http://localhost:3000/release-notes
     */
    APP_RELEASE_NOTES_URL: urlSchema,

    /*
     * API_BASE_URL: String URL
     * Specifies the URL of the API.
     * Required: Yes
     * Example (.env): VITE_API_BASE_URL=http://localhost:3000/api
     */
    API_BASE_URL: urlSchema,

    /*
     * AZURE_AD_AUTH_AUTHORITY: String URL
     * Specifies the authority of the Azure AD application.
     * Required: Yes
     * Example (.env): VITE_AZURE_AD_AUTH_AUTHORITY=https://login.microsoftonline.com/your-tenant-id
     */
    AZURE_AUTH_AUTHORITY_URL: stringSchema,

    /*
     * AZURE_AD_AUTH_CLIENT_ID: String UUID
     * Specifies the client ID of the Azure AD application.
     * Required: Yes
     * Example (.env): VITE_AZURE_AD_AUTH_CLIENT_ID=your-client-id
     */
    AZURE_AUTH_CLIENT_ID: uuidSchema,

    /*
     * AZURE_AD_AUTH_SCOPE: String
     * Specifies the allowed scope of access of the Azure AD application.
     * Required: Yes
     * Example (.env): VITE_AZURE_AD_AUTH_SCOPE=your-client-id/all
     */
    AZURE_AUTH_SCOPE: stringSchema,

    /*
     * AZURE_AD_AUTH_REDIRECT_URL: String URL
     * Specifies the URL to redirect to after successful login.
     * Required: Yes
     * Example (.env): VITE_AZURE_AD_AUTH_REDIRECT_URL=http://localhost:5173
     */
    AZURE_AUTH_REDIRECT_URL: urlSchema,

    /*
     * GTM_ID: String
     * Specifies the Google Tag Manager ID.
     * Required: No
     * Example (.env): VITE_GTM_ID=GTM-12345678
     */
    GTM_ID: stringSchema.optional(),
});

let config: z.infer<typeof configSchema>;
const result = configSchema.safeParse({
    APP_ENV: import.meta.env.VITE_APP_ENV,
    APP_NAME: import.meta.env.VITE_APP_NAME,
    APP_VERSION: import.meta.env.VITE_APP_VERSION,
    API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    APP_RELEASE_NOTES_URL: import.meta.env.VITE_APP_RELEASE_NOTES_URL,
    AZURE_AUTH_AUTHORITY_URL: import.meta.env.VITE_AZURE_AUTH_AUTHORITY_URL,
    AZURE_AUTH_CLIENT_ID: import.meta.env.VITE_AZURE_AUTH_CLIENT_ID,
    AZURE_AUTH_SCOPE: import.meta.env.VITE_AZURE_AUTH_SCOPE,
    AZURE_AUTH_REDIRECT_URL: import.meta.env.VITE_AZURE_AUTH_REDIRECT_URL,
    GTM_ID: import.meta.env.VITE_GTM_ID,
});

class ConfigurationError extends Error {
    name = "ConfigurationError";

    constructor(validationErrors: Record<string, { errors: string[] }>) {
        const formattedErrors = Object.entries(validationErrors)
            .map(([field, { errors }]) => `${field}: ${errors.join(", ")}`)
            .join("\n");

        super(`\n\nErrors while validating environment variables:\n\n${formattedErrors}\n\n`);
    }
}

if (result.success) {
    config = result.data;
} else {
    const structuredErrors = z.treeifyError(result.error);
    throw new ConfigurationError(structuredErrors.properties ?? {});
}

export { config };
